import React, { useEffect, useState, useRef } from 'react';
import Popper from '@mui/material/Popper';
import { fetchStock, fetchStockStats, fetchCategories, fetchBrands, fetchUnits, fetchTaxes, updateProductField, updateVariantField, fetchProductById, fetchAllProducts } from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Grid,
  InputAdornment,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';

const Products = () => {
  // console.log('Products component mounted or re-rendered');
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const preset = query.get('preset');
  const archiveStatus = preset === 'archived-products' ? 'archived' : '';
  const [variants, setVariants] = useState([]);
  const [allVariants, setAllVariants] = useState([]); // for client-side search
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // { variantId, productId, field }
  const [editValue, setEditValue] = useState('');
  const savingCellRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const prevSearchTermRef = useRef('');
  const [page, setPage] = useState(() => {
    const saved = Number(sessionStorage.getItem('pos_products_page'));
    return Number.isInteger(saved) && saved > 1 ? saved : 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = Number(sessionStorage.getItem('pos_products_rpp'));
    return [10, 15, 20, 25, 30, 40, 50, 75, 100].includes(saved) ? saved : 15;
  });
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, inStock: 0, lowStock: 0, outOfStock: 0 });
  
  // Filter state
  const [stockStatus, setStockStatus] = useState(() => preset === 'low-stock' ? 'lowStock' : '');
  const [productStatus, setProductStatus] = useState(() => preset === 'inactive-products' ? 'inactive' : '');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  
  // Sorting state
  const [sortBy, setSortBy] = useState('product_name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Filter options
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  
  // Dialog states
  const [imageZoomDialog, setImageZoomDialog] = useState(false);
  const [zoomedImage, setZoomedImage] = useState('');
  const [productDetailDialog, setProductDetailDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  useEffect(() => {
    setStockStatus(preset === 'low-stock' ? 'lowStock' : '');
    setProductStatus(preset === 'inactive-products' ? 'inactive' : '');
    setPage(1);
  }, [preset]);

  // Function to fetch complete product details
  const handleViewProduct = async (variant) => {
    setLoadingProduct(true);
    setProductDetailDialog(true);
    try {
      const response = await fetchProductById(variant.product_id, archiveStatus === 'archived');
      if (response && response.ok && response.data) {
        setSelectedProduct(response.data);
      } else {
        // Fallback to variant data if API fails
        setSelectedProduct(variant);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      // Fallback to variant data
      setSelectedProduct(variant);
    } finally {
      setLoadingProduct(false);
    }
  };

  // Fetch filter options
  useEffect(() => {
    Promise.all([
      fetchCategories(),
      fetchBrands(),
      fetchUnits(),
      fetchTaxes()
    ]).then(([catRes, brandRes, unitRes, taxRes]) => {
      // Categories returns array directly
      if (Array.isArray(catRes)) {
        setCategories(catRes);
      }
      // Brands returns { data, success }
      if (brandRes?.success && Array.isArray(brandRes.data)) {
        setBrands(brandRes.data);
      }
      // Units returns { data, success }
      if (unitRes?.success && Array.isArray(unitRes.data)) {
        setUnits(unitRes.data);
      }
      // Taxes returns { data, success }
      if (taxRes?.success && Array.isArray(taxRes.data)) {
        setTaxes(taxRes.data);
      }
    }).catch(err => console.error('Error loading filter options:', err));
  }, []);

  // Persist current page so returning from edit/reload keeps the same page
  useEffect(() => {
    if (page >= 1) sessionStorage.setItem('pos_products_page', String(page));
    else sessionStorage.removeItem('pos_products_page');
  }, [page]);

  // Persist selected brand filter
  useEffect(() => {
    sessionStorage.setItem('pos_products_brandId', String(brandId));
  }, [brandId]);

  // Load all products for search once on mount
  useEffect(() => {
    fetchAllProducts().then((res) => {
      let allProducts = res && Array.isArray(res) ? res : (res.data || []);
      let flat = [];
      allProducts.forEach(product => {
        (product.variants || []).forEach(variant => {
          flat.push({
            variant_id: variant.variant_id ?? variant.id,
            product_id: product.id,
            product_name: product.name,
            product_status: product.status,
            barcode: product.barcode,
            hsn: product.hsn,
            image: product.image,
            is_unlimited_stock: product.is_unlimited_stock,
            category_id: product.category_id,
            tax_id: product.tax_id,
            brand_id: product.brand_id,
            category_name: product.category_name,
            brand_name: product.brand_name,
            tax_name: product.tax_name,
            tax_percentage: product.tax_percentage,
            ...variant
          });
        });
      });
      setAllVariants(flat);
    }).catch(() => {});
  }, []);

  // Debounced fetch for filters and client-side search
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      if (searchTerm && searchTerm.trim() !== '' && !archiveStatus) {
        setIsSearching(true);
        const term = searchTerm.toLowerCase();
        const filtered = allVariants.filter(v =>
          (!productStatus || Number(v.product_status) === (productStatus === 'active' ? 1 : 0)) &&
          Object.values(v).some(val =>
            val && val.toString().toLowerCase().includes(term)
          )
        );
        // Search results are cached in allVariants; paginate client-side.
        // Only reset to page 1 when the search term actually changes.
        const termChanged = term !== prevSearchTermRef.current;
        const effectivePage = termChanged ? 1 : page;
        if (termChanged) {
          setPage(1);
          prevSearchTermRef.current = term;
        }
        const safePage = Math.min(effectivePage, Math.max(1, Math.ceil(filtered.length / rowsPerPage)));
        setVariants(filtered.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage));
        setTotal(filtered.length);
        setLoading(false);
      } else {
        setIsSearching(false);
        prevSearchTermRef.current = '';
        const filters = {
          stockStatus: stockStatus || undefined,
          productStatus: productStatus || undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
          brandId: brandId ? Number(brandId) : undefined,
          archiveStatus: archiveStatus || undefined,
          sortBy: sortBy,
          sortOrder: sortOrder
        };
        Promise.all([
          fetchStock(page, rowsPerPage, archiveStatus ? searchTerm : '', filters),
          fetchStockStats(filters)
        ]).then(([r, s]) => {
          if (r && r.ok) {
            if (r.data && r.data.length === 0 && page > 1) {
              setPage(1);
              return;
            }
            setVariants(r.data || []);
            setTotal(r.total || 0);
          } else {
            setVariants([]);
            setTotal(0);
          }
          if (s && s.ok && s.data) {
            setStats(s.data);
          } else {
            setStats({ total: 0, inStock: 0, lowStock: 0, outOfStock: 0 });
          }
        })
        .catch(() => {
          setVariants([]);
          setTotal(0);
          setStats({ total: 0, inStock: 0, lowStock: 0, outOfStock: 0 });
        })
        .finally(() => setLoading(false));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm, stockStatus, productStatus, categoryId, brandId, archiveStatus, sortBy, sortOrder, page, rowsPerPage]);

  const handlePageChange = async (newPage) => {
    if (isSearching) {
      // Client-side pagination for search
      const term = searchTerm.toLowerCase();
      const filtered = allVariants.filter(v =>
        Object.values(v).some(val =>
          val && val.toString().toLowerCase().includes(term)
        )
      );
      const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
      const clamped = Math.min(Math.max(1, newPage), totalPages);
      setVariants(filtered.slice((clamped - 1) * rowsPerPage, clamped * rowsPerPage));
      setPage(clamped);
      setTotal(filtered.length);
    } else {
      setPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newRpp) => {
    setRowsPerPage(newRpp);
    sessionStorage.setItem('pos_products_rpp', String(newRpp));
    setPage(1);
  };

  const handleGoToPage = (e) => {
    if (e.key === 'Enter') {
      const val = parseInt(e.target.value, 10);
      const maxPage = Math.ceil(total / rowsPerPage) || 1;
      if (val >= 1 && val <= maxPage) {
        handlePageChange(val);
      }
      e.target.value = '';
    }
  };

  const handleSort = async (field) => {
    const newSortOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const isOutOfStock = (stock) => stock === 0;
  const isLowStock = (stock) => stock > 0 && stock < 10;

  const getStockColor = (stock) => {
    if (isOutOfStock(stock)) return 'error';
    if (isLowStock(stock)) return 'warning';
    return 'success';
  };

  const handleEditCell = (variantId, productId, field, currentValue) => {
    setEditingCell({ variantId, productId, field });
    setEditValue(currentValue !== undefined && currentValue !== null ? currentValue : '');
  };

  const handleSaveCell = async () => {
    if (!editingCell || savingCellRef.current) return;
    savingCellRef.current = true;

    const { variantId, productId, field } = editingCell;
    const rawValue = editValue;

    // Fields stored on the product record (shared by every variant of that product)
    const productFields = ['category_id', 'brand_id', 'barcode', 'tax_id', 'product_name', 'name', 'hsn'];
    const variantFields = ['stock', 'discounted_price', 'stock_unit_id', 'price', 'status', 'product_short_name'];
    const isProductField = productFields.includes(field);

    // Build the patched row for the edited field
    const patchRow = (v) => {
      switch (field) {
        case 'category_id': return { ...v, category_id: parseInt(rawValue), category_name: categories.find(c => c.id === parseInt(rawValue))?.name };
        case 'brand_id': return { ...v, brand_id: parseInt(rawValue), brand_name: brands.find(b => b.id === parseInt(rawValue))?.name };
        case 'barcode': return { ...v, barcode: rawValue };
        case 'tax_id': {
          const tax = taxes.find(t => t.id === parseInt(rawValue));
          return { ...v, tax_id: parseInt(rawValue), tax_name: tax?.title, tax_percentage: tax?.percentage };
        }
        case 'stock_unit_id': return { ...v, stock_unit_id: parseInt(rawValue), unit_name: units.find(u => u.id === parseInt(rawValue))?.name };
        case 'stock': return { ...v, stock: parseFloat(rawValue) };
        case 'discounted_price': return { ...v, discounted_price: parseFloat(rawValue) };
        case 'price': return { ...v, price: parseFloat(rawValue) };
        case 'status': return { ...v, status: parseInt(rawValue) };
        case 'product_name':
        case 'name': return { ...v, product_name: rawValue };
        case 'product_short_name': return { ...v, product_short_name: rawValue };
        case 'hsn': return { ...v, hsn: rawValue };
        default: return v;
      }
    };

    // Optimistically update the UI immediately so every affected row reflects
    // the change without waiting for the network round-trip or a refresh.
    const applyUpdate = (list) => list.map(v => {
      const matches = isProductField
        ? Number(v.product_id) === Number(productId)
        : Number(v.variant_id) === Number(variantId);
      return matches ? patchRow(v) : v;
    });
    setVariants(prev => applyUpdate(prev));
    setAllVariants(prev => applyUpdate(prev));
    setEditingCell(null);
    setEditValue('');

    // Persist to the backend
    try {
      if (isProductField) {
        // product_name is mapped to name in backend
        const backendField = field === 'product_name' ? 'name' : field;
        await updateProductField(productId, backendField, rawValue);
      } else if (variantFields.includes(field)) {
        let value;
        if (field === 'product_short_name') {
          value = rawValue;
        } else if (field === 'status') {
          value = parseInt(rawValue);
        } else {
          value = parseFloat(rawValue);
        }
        await updateVariantField(variantId, field, value);
      }

      // Refresh stats
      const filters = {
        stockStatus: stockStatus || undefined,
        categoryId: categoryId ? Number(categoryId) : undefined
      };
      const statsRes = await fetchStockStats(filters);
      if (statsRes?.ok && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('Error updating field:', err);
    } finally {
      savingCellRef.current = false;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="product-page" sx={{ mt: 2, mb: 2, px: 2 }}>
      <Box className="product-hero">
        <Box>
          <Box className="product-breadcrumb"><StorageIcon sx={{ fontSize: 16 }} /> Catalog / Inventory</Box>
          <Typography variant="h4" className="product-heading">Products &amp; stock</Typography>
          <Typography className="product-subheading">Keep your retail catalog accurate, searchable, and ready for sale.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/products/bulk-update')}>Bulk update</Button>
          <Button variant="contained" onClick={() => navigate('/products/new')}>Add product</Button>
        </Box>
      </Box>

      <Box className="product-toolbar">

        {/* Stats, Search, and Filters in one row */}
        <Box className="product-stat-strip" sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            {/* Stats Chips */}
          <Box className="product-stat-cards" sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Chip
                label={`Total: ${stats.total}`}
                size="small"
                sx={{ height: 24, bgcolor: '#1976d2', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}
              />
              <Chip
                label={`In: ${stats.inStock}`}
                size="small"
                color="success"
                sx={{ height: 24, fontWeight: 500, fontSize: '0.7rem' }}
              />
              <Chip
                label={`Out: ${stats.outOfStock}`}
                size="small"
                color="error"
                sx={{ height: 24, fontWeight: 500, fontSize: '0.7rem' }}
              />
              <Chip
                label={`Low: ${stats.lowStock}`}
                size="small"
                color="warning"
                sx={{ height: 24, fontWeight: 500, fontSize: '0.7rem' }}
              />
            </Box>

            {/* Search */}
            <TextField
              size="small"
              placeholder="Search products..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 250,
                '& .MuiOutlinedInput-root': { fontSize: '0.8rem' }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                      <CloseIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />

            {/* Filters */}
            <Autocomplete
              size="small"
              options={[
                { value: '', label: 'All' },
                { value: 'inStock', label: 'In Stock' },
                { value: 'lowStock', label: 'Low Stock' },
                { value: 'outOfStock', label: 'Out of Stock' }
              ]}
              getOptionLabel={(option) => option.label}
              value={[{ value: '', label: 'All' }, { value: 'inStock', label: 'In Stock' }, { value: 'lowStock', label: 'Low Stock' }, { value: 'outOfStock', label: 'Out of Stock' }].find(o => o.value === stockStatus) || null}
              onChange={(_, newValue) => setStockStatus(newValue ? newValue.value : '')}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stock Status"
                  placeholder="Search status..."
                  sx={{ fontSize: '0.8rem', minWidth: 170 }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.value} style={{ fontSize: '0.85rem' }}>
                  {option.label}
                </li>
              )}
              noOptionsText="No status found"
              sx={{ minWidth: 170 }}
            />

            <Autocomplete
              size="small"
              options={categories}
              getOptionLabel={(option) => option.name || ''}
              value={categories.find(c => String(c.id) === String(categoryId)) || null}
              onChange={(_, newValue) => setCategoryId(newValue ? String(newValue.id) : '')}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  placeholder="Search category..."
                  sx={{ fontSize: '0.8rem', minWidth: 180 }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ fontSize: '0.85rem' }}>
                  {option.name}
                </li>
              )}
              noOptionsText="No category found"
              sx={{ minWidth: 180 }}
            />

            <Autocomplete
              size="small"
              options={brands}
              getOptionLabel={(option) => option.name || ''}
              value={brands.find(b => String(b.id) === String(brandId)) || null}
              onChange={(_, newValue) => setBrandId(newValue ? String(newValue.id) : '')}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  placeholder="Search brand..."
                  sx={{ fontSize: '0.8rem', minWidth: 180 }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ fontSize: '0.85rem' }}>
                  {option.name}
                </li>
              )}
              noOptionsText="No brand found"
              sx={{ minWidth: 180 }}
            />
          </Box>
      </Box>

      {/* Stock Table */}
      {variants.length === 0 ? (
        <Paper sx={{ py: 3, px: 2, textAlign: 'center', bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
          <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {searchTerm ? 'No variants match your search.' : 'No variants with limited stock found.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer className="product-table-shell" component={Paper} sx={{
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1
        }}>
          <Table className="product-table" size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: '#f5f5f5'
              }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Image</TableCell>
                <TableCell 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', cursor: 'pointer', userSelect: 'none', borderBottom: '2px solid', borderColor: 'divider' }}
                  onClick={() => handleSort('product_name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Product Name {sortBy === 'product_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Short Name</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Barcode</TableCell>
                <TableCell 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', cursor: 'pointer', userSelect: 'none', borderBottom: '2px solid', borderColor: 'divider' }} 
                  align="right"
                  onClick={() => handleSort('stock')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    Stock {sortBy === 'stock' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }} align="right">Unit</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }} align="right">Tax</TableCell>
                <TableCell 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', cursor: 'pointer', userSelect: 'none', borderBottom: '2px solid', borderColor: 'divider' }} 
                  align="right"
                  onClick={() => handleSort('hsn')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    HSN {sortBy === 'hsn' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }} align="right">Price</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }} align="right">Sale Price</TableCell>
                <TableCell 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', cursor: 'pointer', userSelect: 'none', borderBottom: '2px solid', borderColor: 'divider' }} 
                  align="center"
                  onClick={() => handleSort('status')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((variant) => (
                <TableRow
                  key={variant.variant_id}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover'
                    },
                    '& .MuiTableCell-root': {
                      fontSize: '0.8rem'
                    }
                  }}
                >
                  <TableCell sx={{ py: 0.5 }}>
                    <Box 
                      sx={{ 
                        position: 'relative',
                        width: 50,
                        height: 50,
                        cursor: 'pointer',
                        '&:hover .zoom-overlay': {
                          opacity: 1
                        }
                      }}
                      onClick={() => {
                        if (variant.image) {
                          setZoomedImage(`https://martadmin.owntrip.in/storage/${variant.image}`);
                          setImageZoomDialog(true);
                        }
                      }}
                    >
                      <Avatar
                        src={variant.image ? `https://martadmin.owntrip.in/storage/${variant.image}` : ''}
                        variant="rounded"
                        sx={{ width: 50, height: 50, bgcolor: '#f5f5f5' }}
                      >
                        {!variant.image && variant.product_name?.charAt(0)}
                      </Avatar>
                      {variant.image && (
                        <Box 
                          className="zoom-overlay"
                          sx={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            borderRadius: 1
                          }}
                        >
                          <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {editingCell?.field === 'product_name' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', '& input': { padding: '4px 8px', fontSize: '0.8rem', fontWeight: 500 } }}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontWeight: 500, fontSize: '0.8rem', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'product_name', variant.product_name)}
                      >
                        {variant.product_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {editingCell?.field === 'product_short_name' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', '& input': { padding: '4px 8px', fontSize: '0.8rem', fontWeight: 500 } }}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontWeight: 500, fontSize: '0.8rem', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'product_short_name', variant.product_short_name)}
                      >
                        {variant.product_short_name || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {editingCell?.field === 'category_id' && editingCell?.variantId === variant.variant_id ? (
                      <Autocomplete
                        size="small"
                        options={categories}
                        getOptionLabel={(option) => option.name || ''}
                        value={categories.find(cat => cat.id === Number(editValue)) || null}
                        onChange={(_, newValue) => setEditValue(newValue ? newValue.id : '')}
                        renderInput={(params) => <TextField {...params}  onBlur={handleSaveCell} onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()} />}
                        sx={{ width: '100%', '& .MuiAutocomplete-listbox': { fontSize: '0.8rem' } }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        PopperComponent={(props) => <Popper {...props} style={{ minWidth: 200, width: 'auto', maxWidth: 400 }} placement="bottom-start" />}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id} style={{ fontSize: '0.8rem' }}>
                            {option.name}
                          </li>
                        )}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'category_id', variant.category_id)}
                      >
                        {variant.category_name || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {editingCell?.field === 'brand_id' && editingCell?.variantId === variant.variant_id ? (
                      <Autocomplete
                        size="small"
                        options={brands}
                        getOptionLabel={(option) => option.name || ''}
                        value={brands.find(brand => brand.id === Number(editValue)) || null}
                        onChange={(_, newValue) => setEditValue(newValue ? newValue.id : '')}
                        renderInput={(params) => <TextField {...params}  onBlur={handleSaveCell} onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()} />}
                        sx={{ width: '100%', '& .MuiAutocomplete-listbox': { fontSize: '0.8rem' } }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        PopperComponent={(props) => <Popper {...props} style={{ minWidth: 200, width: 'auto', maxWidth: 400 }} placement="bottom-start" />}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id} style={{ fontSize: '0.8rem' }}>
                            {option.name}
                          </li>
                        )}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'brand_id', variant.brand_id)}
                      >
                        {variant.brand_name || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {editingCell?.field === 'barcode' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', '& input': { padding: '4px 8px', fontSize: '0.8rem' } }}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'barcode', variant.barcode)}
                      >
                        {variant.barcode || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editingCell?.field === 'stock' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{
                          width: '80px',
                          '& input': {
                            textAlign: 'center',
                            fontWeight: 600,
                            padding: '4px 8px',
                            color: getStockColor(parseFloat(editValue) || variant.stock) === 'error' ? 'error.main' : getStockColor(parseFloat(editValue) || variant.stock) === 'warning' ? 'warning.main' : 'success.main'
                          },
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: 'primary.main'
                            }
                          }
                        }}
                      />
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={variant.stock}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'stock', variant.stock)}
                        readOnly
                        sx={{
                          width: '80px',
                          '& input': {
                            textAlign: 'center',
                            fontWeight: 600,
                            padding: '4px 8px',
                            color: getStockColor(variant.stock) === 'error' ? 'error.main' : getStockColor(variant.stock) === 'warning' ? 'warning.main' : 'success.main',
                            cursor: 'pointer'
                          },
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: 'primary.main'
                            }
                          }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {editingCell?.field === 'stock_unit_id' && editingCell?.variantId === variant.variant_id ? (
                      <Select
                        size="small"
                        value={editValue || ''}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', height: '32px' }}
                      >
                        {units.map(unit => (
                          <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'stock_unit_id', variant.stock_unit_id)}
                      >
                        {variant.unit_name || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {editingCell?.field === 'tax_id' && editingCell?.variantId === variant.variant_id ? (
                      <Select
                        size="small"
                        value={editValue || ''}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', height: '32px' }}
                      >
                        {taxes.map(tax => (
                          <MenuItem key={tax.id} value={tax.id}>{tax.title} ({tax.percentage}%)</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'tax_id', variant.tax_id)}
                      >
                        {variant.tax_name ? `${variant.tax_name} (${variant.tax_percentage}%)` : '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {editingCell?.field === 'hsn' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', '& input': { padding: '4px 8px', fontSize: '0.8rem' } }}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'hsn', variant.hsn || '')}
                      >
                        {variant.hsn || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {editingCell?.field === 'price' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '80px', '& input': { textAlign: 'center', padding: '4px 8px', fontSize: '0.8rem' } }}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'price', variant.price)}
                      >
                        ₹{variant.price?.toFixed(2) || '0.00'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {editingCell?.field === 'discounted_price' && editingCell?.variantId === variant.variant_id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '80px', '& input': { textAlign: 'center', padding: '4px 8px', fontSize: '0.8rem' } }}
                      />
                    ) : (
                      <Typography 
                        sx={{ fontSize: '0.8rem', fontWeight: 500, color: variant.discounted_price > 0 ? '#d32f2f' : '#999', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'discounted_price', variant.discounted_price)}
                      >
                        {variant.discounted_price > 0 ? `₹${variant.discounted_price.toFixed(2)}` : '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    {editingCell?.field === 'status' && editingCell?.variantId === variant.variant_id ? (
                      <Select
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveCell}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveCell()}
                        
                        sx={{ width: '100%', height: '32px' }}
                      >
                        <MenuItem value={1}>Available</MenuItem>
                        <MenuItem value={0}>Sold Out</MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        label={variant.status === 1 ? 'Available' : 'Sold Out'}
                        size="small"
                        color={variant.status === 1 ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: '24px', fontSize: '0.75rem', cursor: 'pointer' }}
                        onClick={() => handleEditCell(variant.variant_id, variant.product_id, 'status', variant.status)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton 
                        size="small"
                        color="primary"
                        onClick={() => handleViewProduct(variant)}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: 'rgba(25, 118, 210, 0.08)' 
                          } 
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        color="warning"
                        onClick={() => navigate(`/products/edit/${variant.product_id}${archiveStatus ? '?archived=1' : ''}`)}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: 'rgba(237, 108, 2, 0.08)' 
                          } 
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {total > rowsPerPage && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              Go to page:
            </Typography>
            <TextField
              size="small"
              type="number"
              placeholder="#"
              onKeyDown={handleGoToPage}
              sx={{
                width: 65,
                '& .MuiOutlinedInput-root': { fontSize: '0.8rem', height: '30px' },
                '& input': { textAlign: 'center', p: '4px 6px' }
              }}
              inputProps={{ min: 1, max: Math.ceil(total / rowsPerPage) }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              Rows per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
              sx={{ fontSize: '0.8rem', height: '30px', minWidth: 70 }}
            >
              {[10, 15, 20, 25, 30, 40, 50, 75, 100].map(rpp => (
                <MenuItem key={rpp} value={rpp}>{rpp}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1 || loading}
              variant="outlined"
              size="small"
              sx={{
                fontSize: '0.75rem',
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': { borderColor: '#1565c0', bgcolor: 'rgba(25, 118, 210, 0.04)' },
                '&:disabled': { borderColor: 'divider', color: 'text.disabled' }
              }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              Page {page} of {Math.ceil(total / rowsPerPage)} ({total} total)
            </Typography>
            <Button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= Math.ceil(total / rowsPerPage) || loading}
              variant="outlined"
              size="small"
              sx={{
                fontSize: '0.75rem',
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': { borderColor: '#1565c0', bgcolor: 'rgba(25, 118, 210, 0.04)' },
                '&:disabled': { borderColor: 'divider', color: 'text.disabled' }
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Image Zoom Dialog */}
      <Dialog
        open={imageZoomDialog}
        onClose={() => setImageZoomDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Product Image</Typography>
          <IconButton size="small" onClick={() => setImageZoomDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <img 
            src={zoomedImage} 
            alt="Product" 
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>

      {/* Product Detail Dialog */}
      <Dialog
        className="product-detail-dialog"
        open={productDetailDialog}
        onClose={() => setProductDetailDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle className="product-detail-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5, bgcolor: '#1976d2', color: 'white' }}>
          <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
            {selectedProduct?.name || selectedProduct?.product_name || 'Product Details'}
          </Typography>
          <IconButton size="small" onClick={() => setProductDetailDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="product-detail-content" sx={{ mt: 2 }}>
          {loadingProduct ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : selectedProduct && (
            <Box sx={{
              '& .MuiTypography-root': { fontSize: '0.85rem' },
              '& .MuiTableCell-root': { fontSize: '0.8rem', py: 0.75 },
              '& .MuiChip-root': { height: 22 },
              '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
              '& .MuiChip-icon': { fontSize: 16 },
              '& h6.MuiTypography-root': { fontSize: '0.95rem' },
              '& .MuiAvatar-root': { fontSize: '2.25rem' }
            }}>
              {/* Product Basic Info Section (Compact) */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                {/* Product Image */}
                <Box sx={{ width: 200, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  {(selectedProduct.image || selectedProduct.product_image) ? (
                    <img
                      src={`https://martadmin.owntrip.in/storage/${selectedProduct.image || selectedProduct.product_image}`}
                      alt={selectedProduct.name || selectedProduct.product_name}
                      style={{ width: 200, height: 200, objectFit: 'contain', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                    />
                  ) : (
                    <Avatar variant="rounded" sx={{ width: 200, height: 200, bgcolor: '#f5f5f5', fontSize: '3rem' }}>
                      {(selectedProduct.name || selectedProduct.product_name)?.charAt(0)}
                    </Avatar>
                  )}
                </Box>

                {/* Product Info (Organized Table) */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    Product Information
                  </Typography>
                  <TableContainer sx={{ maxHeight: 340 }}>
                    <Table size="small" stickyHeader={false}>
                      <TableBody>
                        {(() => {
                          const totalStock = (selectedProduct.variants?.reduce((s, v) => s + Number(v.stock || 0), 0) || selectedProduct.stock || 0);
                          const entries = [
                            { label: 'Product ID', value: selectedProduct.id || selectedProduct.product_id },
                            { label: 'Seller ID', value: selectedProduct.seller_id || '-' },
                            { label: 'Status', value: (
                              <Chip label={selectedProduct.status === 1 ? 'Active' : 'Inactive'} size="small" color={selectedProduct.status === 1 ? 'success' : 'default'} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                            ) },
                            { label: 'Name', value: (<Typography sx={{ fontWeight: 600 }}>{selectedProduct.name || selectedProduct.product_name}</Typography>) },
                            { label: 'Category', value: selectedProduct.category_name || (selectedProduct.category_id ? `ID: ${selectedProduct.category_id}` : '-') },
                            { label: 'Brand', value: selectedProduct.brand_name || (selectedProduct.brand_id ? `ID: ${selectedProduct.brand_id}` : '-') },
                            { label: 'Barcode', value: selectedProduct.barcode || '-' },
                            { label: 'Type', value: selectedProduct.type || '-' },
                            { label: 'Indicator', value: (
                              <Chip label={selectedProduct.indicator === 1 ? 'Veg' : selectedProduct.indicator === 2 ? 'Non-Veg' : 'None'} size="small" color={selectedProduct.indicator === 1 ? 'success' : selectedProduct.indicator === 2 ? 'error' : 'default'} sx={{ height: 22, fontSize: '0.7rem' }} />
                            ) },
                            { label: 'Stock', value: (<Typography sx={{ fontWeight: 700, color: totalStock <= 0 ? 'error.main' : totalStock <= 10 ? 'warning.main' : 'success.main' }}>{totalStock}</Typography>) },
                            { label: 'Unlimited', value: (<Chip label={selectedProduct.is_unlimited_stock === 1 ? 'Yes' : 'No'} size="small" color={selectedProduct.is_unlimited_stock === 1 ? 'primary' : 'default'} sx={{ height: 22, fontSize: '0.7rem' }} />) },
                            { label: 'Tax', value: selectedProduct.tax_id || '0' },
                            { label: 'Tax Incl.', value: (<Chip label={selectedProduct.tax_included_in_price === 1 ? 'Yes' : 'No'} size="small" color={selectedProduct.tax_included_in_price === 1 ? 'info' : 'default'} sx={{ height: 22, fontSize: '0.7rem' }} />) },
                            { label: 'COD', value: (<Chip label={selectedProduct.cod_allowed === 1 ? 'Yes' : 'No'} size="small" color={selectedProduct.cod_allowed === 1 ? 'success' : 'default'} sx={{ height: 22, fontSize: '0.7rem' }} />) },
                            { label: 'Cancelable', value: (<Chip label={selectedProduct.cancelable_status === 1 ? 'Yes' : 'No'} size="small" color={selectedProduct.cancelable_status === 1 ? 'info' : 'default'} sx={{ height: 22, fontSize: '0.7rem' }} />) },
                            { label: 'Return', value: (<Chip label={selectedProduct.return_status === 1 ? 'Allowed' : 'Not Allowed'} size="small" color={selectedProduct.return_status === 1 ? 'info' : 'default'} sx={{ height: 22, fontSize: '0.7rem' }} />) },
                            { label: 'Return Days', value: selectedProduct.return_days || 0 },
                            { label: 'Manufacturer', value: selectedProduct.manufacturer || '-' },
                            { label: 'Made In', value: selectedProduct.made_in || '-' },
                            { label: 'FSSAI', value: selectedProduct.fssai_lic_no || '-' },
                            { label: 'Created', value: selectedProduct.created_at ? new Date(selectedProduct.created_at).toLocaleString('en-IN') : '-' },
                            { label: 'Updated', value: selectedProduct.updated_at ? new Date(selectedProduct.updated_at).toLocaleString('en-IN') : '-' },
                          ];
                          const rows = [];
                          for (let i = 0; i < entries.length; i += 2) {
                            rows.push([
                              entries[i],
                              entries[i + 1] || null
                            ]);
                          }
                          return (
                            <>
                              {rows.map((pair, idx) => (
                                <TableRow key={`info-row-${idx}`}>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>{pair[0].label}</TableCell>
                                  <TableCell>{pair[0].value}</TableCell>
                                  {pair[1] ? (
                                    <>
                                      <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>{pair[1].label}</TableCell>
                                      <TableCell>{pair[1].value}</TableCell>
                                    </>
                                  ) : (
                                    <TableCell colSpan={2} />
                                  )}
                                </TableRow>
                              ))}
                              {selectedProduct.tags && (
                                <TableRow>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>Tags</TableCell>
                                  <TableCell colSpan={3} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{selectedProduct.tags}</TableCell>
                                </TableRow>
                              )}
                              {selectedProduct.slug && (
                                <TableRow>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>Slug</TableCell>
                                  <TableCell colSpan={3} sx={{ wordBreak: 'break-word' }}>{selectedProduct.slug}</TableCell>
                                </TableRow>
                              )}
                              {selectedProduct.meta_title && (
                                <TableRow>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>Meta Title</TableCell>
                                  <TableCell colSpan={3} sx={{ wordBreak: 'break-word' }}>{selectedProduct.meta_title}</TableCell>
                                </TableRow>
                              )}
                              {selectedProduct.meta_keywords && (
                                <TableRow>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>Meta Keywords</TableCell>
                                  <TableCell colSpan={3} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{selectedProduct.meta_keywords}</TableCell>
                                </TableRow>
                              )}
                              {selectedProduct.meta_description && (
                                <TableRow>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>Meta Description</TableCell>
                                  <TableCell colSpan={3} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{selectedProduct.meta_description}</TableCell>
                                </TableRow>
                              )}
                              {selectedProduct.description && (
                                <TableRow>
                                  <TableCell sx={{ width: 160, fontWeight: 600, color: 'text.secondary' }}>Description</TableCell>
                                  <TableCell colSpan={3} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    <Box
                                      sx={{
                                        '& p': { m: 0, mb: 0.5, fontSize: '0.9rem' },
                                        '& strong': { fontWeight: 700 },
                                        '& br': { display: 'block', mb: 0.5 }
                                      }}
                                      dangerouslySetInnerHTML={{ __html: String(selectedProduct.description) }}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>

              {/* Sales Analytics (Compact) */}
              <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon /> Key Metrics
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip icon={<TrendingUpIcon />} label={`Units: ${selectedProduct.total_units_sold || 0}`} size="small" sx={{ height: 26 }} />
                <Chip icon={<AttachMoneyIcon />} label={`Revenue: ₹${Number(selectedProduct.total_revenue || 0).toFixed(2)}`} size="small" sx={{ height: 26 }} />
                <Chip icon={<ShoppingCartIcon />} label={`Orders: ${selectedProduct.total_orders || 0}`} size="small" sx={{ height: 26 }} />
                <Chip icon={<BarChartIcon />} label={`Avg/Order: ${selectedProduct.avg_quantity_per_order || 0}`} size="small" sx={{ height: 26 }} />
              </Box>

              {/* Sale Dates */}
              {(selectedProduct.first_sale_date || selectedProduct.last_sale_date) && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    {selectedProduct.first_sale_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          First Sale:
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedProduct.first_sale_date).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Grid>
                    )}
                    {selectedProduct.last_sale_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Last Sale:
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedProduct.last_sale_date).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            
              {/* Monthly Sales Trend */}
              {selectedProduct.monthly_sales && selectedProduct.monthly_sales.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
                    Monthly Sales (Last 6 Months)
                  </Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Units Sold</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Revenue</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Orders</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProduct.monthly_sales.map((ms) => (
                          <TableRow key={ms.month} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(ms.month + '-01').toLocaleDateString('en-IN', { year: 'numeric', month: 'short' })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{Number(ms.units_sold)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                ₹{Number(ms.revenue || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{Number(ms.order_count)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Variants Section (Dense Table) */}
              {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    Variants ({selectedProduct.variants.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 1, maxHeight: 240 }}>
                    <Table size="small" stickyHeader>
                      <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Variant</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Sale</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Stock</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProduct.variants.map((variant) => (
                          <TableRow key={variant.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {variant.type} - {variant.measurement} {variant.unit_short_code || variant.unit_name || ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">₹{Number(variant.price).toFixed(2)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: Number(variant.discounted_price) > 0 ? 'success.main' : 'text.disabled', fontWeight: 600 }}>
                                {Number(variant.discounted_price) > 0 ? `₹${Number(variant.discounted_price).toFixed(2)}` : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: Number(variant.stock) <= 0 ? 'error.main' : Number(variant.stock) <= 10 ? 'warning.main' : 'success.main' }}>
                                {Number(variant.stock)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={variant.status === 1 ? 'Available' : 'Sold Out'} size="small" color={variant.status === 1 ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProductDetailDialog(false)} variant="contained" sx={{ bgcolor: '#1976d2' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Products;
